import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Select, Row, Col, Button, message, Typography, Divider, Space } from "antd";
import { SaveOutlined, ArrowLeftOutlined, EditOutlined, CalendarOutlined, FileTextOutlined } from "@ant-design/icons";
import { Method_POST_CONTEXTO } from "../../../../../service/api";
import { useDraggableModal } from "../../Balancete/utils/modalDraggable";

const { Title, Text } = Typography;

const { Option } = Select;

export function ModalLancamentoEditarLote({ open, onClose, lancamentos, onSave, zIndex, onFocus }) {
    const draggableProps = useDraggableModal(zIndex, onFocus, { open, modalKey: 'editBancarioLote' });
    const [form] = Form.useForm();
    const [grupos, setGrupos] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [loadingGrupos, setLoadingGrupos] = useState(false);
    const [loadingCategorias, setLoadingCategorias] = useState(false);

    const fetchGrupos = async (tipoId) => {
        try {
            setLoadingGrupos(true);
            const response = await Method_POST_CONTEXTO("/contabil/lancamento/categoria/grupo/listar", { tipo: { id: tipoId } });
            setGrupos(response || []);
        } catch (err) {
            console.error(err);
            message.error("Erro ao carregar grupos");
        } finally {
            setLoadingGrupos(false);
        }
    };

    const fetchCategorias = async (grupoId) => {
        try {
            setLoadingCategorias(true);
            const response = await Method_POST_CONTEXTO("/contabil/lancamento/categoria/listar", { grupo: { id: grupoId }, situacao: { id: "A" } });
            setCategorias(response || []);
        } catch (err) {
            console.error(err);
            message.error("Erro ao carregar categorias");
        } finally {
            setLoadingCategorias(false);
        }
    };

    useEffect(() => {
        if (open && lancamentos?.length > 0) {
            fetchGrupos(lancamentos[0].tipo.id);
        }
    }, [open, lancamentos]);

    const handleSave = async () => {
        try {
            const values = await form.validateFields();

            const payloads = lancamentos.map((l) => ({
                ...l, // mantém todos os campos existentes
                grupo: values.grupo ? { id: values.grupo } : l.grupo,
                categoria: values.categoria ? { id: values.categoria } : l.categoria,
                descricao: values.descricao || l.descricao,
            }));

            await Promise.all(payloads.map((p) =>
                Method_POST_CONTEXTO("contabil/lancamento/atualizar", p)
            ));

            message.success("Lançamentos atualizados com sucesso!");
            onSave(payloads);
            onClose();
            form.resetFields();
        } catch (err) {
            console.error(err);
            message.error("Erro ao atualizar lançamentos");
        }
    };

    return (
        <Modal
            open={open}
            onCancel={onClose}
            width={800}
            footer={null}
            destroyOnClose
            {...draggableProps}
            className="premium-modal-v2"
            closable={true}
            styles={{
                body: {
                    height: draggableProps.style?.height ? "calc(100% - 130px)" : "70vh",
                    padding: '24px',
                    overflowY: "auto",
                    overflowX: "hidden"
                }
            }}
        >
            <div style={{ padding: '8px' }}>
                <header style={{
                    marginBottom: 32,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 16,
                    borderBottom: '1px solid #f1f5f9',
                    paddingBottom: 20
                }}>
                    <div style={{
                        width: 56,
                        height: 56,
                        borderRadius: 16,
                        backgroundColor: '#f8fafc',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#475569',
                        fontSize: 24,
                        boxShadow: `0 4px 12px rgba(71, 85, 105, 0.12)`
                    }}>
                        <EditOutlined />
                    </div>
                    <div>
                        <Title level={4} style={{ margin: 0, color: '#0f172a' }}>
                            Editar Lotes ({lancamentos?.length || 0} lançamentos)
                        </Title>
                        <Text type="secondary">Preencha apenas os campos que deseja alterar em massa para todos os lançamentos selecionados.</Text>
                    </div>
                </header>

                <Form form={form} layout="vertical" requiredMark={false}>
                    <Row gutter={[24, 0]}>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="grupo"
                                label={<Space><CalendarOutlined /> Grupo de Lançamento</Space>}
                            >
                                <Select
                                    size="large"
                                    className="premium-select"
                                    placeholder="Manter original (Selecione para alterar)"
                                    loading={loadingGrupos}
                                    onChange={fetchCategorias}
                                    allowClear
                                >
                                    {grupos.map((g) => <Option key={g.id} value={g.id}>{g.nome}</Option>)}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="categoria"
                                label={<Space><FileTextOutlined /> Categoria / Conta</Space>}
                            >
                                <Select
                                    size="large"
                                    className="premium-select"
                                    placeholder="Manter original (Selecione para alterar)"
                                    loading={loadingCategorias}
                                    allowClear
                                    disabled={!categorias.length}
                                >
                                    {categorias.map((c) => <Option key={c.id} value={c.id}>{c.nome}</Option>)}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24}>
                            <Form.Item
                                name="descricao"
                                label="Descrição / Histórico"
                            >
                                <Input.TextArea
                                    placeholder="Deixe em branco para manter as descrições originais..."
                                    className="premium-textarea"
                                    rows={2}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <div
                        className="footer-buttons"
                        style={{
                            marginTop: 40,
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 12,
                            paddingTop: 24,
                            borderTop: '1px solid #f1f5f9',
                            flexWrap: 'wrap'
                        }}
                    >
                        <Button
                            icon={<ArrowLeftOutlined />}
                            onClick={onClose}
                            size="large"
                            className="premium-btn-secondary"
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            onClick={handleSave}
                            size="large"
                            className="premium-btn-primary"
                        >
                            Aplicar Alterações em Lote
                        </Button>
                    </div>
                </Form>
            </div>
        </Modal>
    );
}
